import React, { createContext, useState, useEffect } from 'react';
import { getCompanyCode } from './CompanyContext';
import { clearAuthData, readAuthData, readLastActivity, writeAuthData, writeLastActivity } from '../utils/authStorage';

// Create the AuthContext with default value null (no user logged in)
export const AuthContext = createContext(null);

// Auto-logout after this much inactivity. Change this one value to adjust.
const IDLE_LIMIT_MS = 30 * 60 * 1000; // 30 minutes
// Throttle activity bookkeeping so we don't thrash localStorage on mousemove.
const ACTIVITY_THROTTLE_MS = 5 * 1000;
// User actions that count as "activity" and keep the session alive.
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

export const AuthProvider = ({ children }) => {
  const [authData, setAuthData] = useState(() => {
    const storedData = readAuthData();
    if (!storedData) return null;
    // If the tab was closed and reopened after the idle window elapsed, stay
    // logged out (the session shouldn't survive long inactivity).
    const last = readLastActivity();
    if (last && Date.now() - last > IDLE_LIMIT_MS) {
      clearAuthData();
      return null;
    }
    return storedData;
  });

  // Function to log in the user
  const login = (data) => {
    setAuthData(data);
    console.log(data);
    writeAuthData(data);
    writeLastActivity();
  };

  // Function to log out the user (manual logout button)
  const logout = () => {
    setAuthData(null);
    clearAuthData();
  };

  // Idle auto-logout: a forgotten/abandoned session closes itself after
  // IDLE_LIMIT_MS of no user activity, instead of staying open until manual
  // logout. Hard-reloads to '/' so all in-memory data is cleared and the app
  // re-renders the login screen.
  useEffect(() => {
    if (!authData) return undefined;

    let timerId;
    let lastWrite = 0;

    const idleLogout = () => {
      // Respect activity from OTHER tabs (shared via localStorage): only really
      // log out once every tab has been idle for the full window.
      const last = readLastActivity();
      const elapsed = Date.now() - last;
      if (last && elapsed < IDLE_LIMIT_MS) {
        clearTimeout(timerId);
        timerId = setTimeout(idleLogout, IDLE_LIMIT_MS - elapsed);
        return;
      }
      clearAuthData();
      const cc = getCompanyCode();
      window.location.assign(cc ? `/${cc}/` : '/');
    };

    const resetTimer = () => {
      clearTimeout(timerId);
      timerId = setTimeout(idleLogout, IDLE_LIMIT_MS);
    };

    const markActivity = () => {
      const now = Date.now();
      if (now - lastWrite < ACTIVITY_THROTTLE_MS) return; // throttle
      lastWrite = now;
      writeLastActivity(now);
      resetTimer();
    };

    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, markActivity, { passive: true })
    );
    writeLastActivity();
    resetTimer();

    // If the tab was hidden/asleep longer than the limit, log out on return.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const last = readLastActivity();
      if (last && Date.now() - last > IDLE_LIMIT_MS) idleLogout();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(timerId);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActivity));
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [authData]);

  return (
    <AuthContext.Provider value={{ authData, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
